/**
 * Health Check Service - Web
 * Browser-compatible health checks using fetch API
 */

import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:service:health-check-service');

import {
  COMMON_SUGGESTIONS,
  ExternalService,
  HealthCheckReport,
  PERFORMANCE_THRESHOLDS,
  SERVICE_INFO,
  ServiceHealthCheck,
  ServiceHealthStatus,
} from '@ctrl-enter/types';

/**
 * Test connectivity to a single service using fetch
 */
const testServiceHealth = async (service: ExternalService): Promise<ServiceHealthCheck> => {
  const serviceInfo = SERVICE_INFO[service];
  const startTime = performance.now();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), serviceInfo.timeout);

  try {
    const response = await fetch(serviceInfo.testEndpoint, {
      method: 'HEAD',
      signal: controller.signal,
      mode: 'no-cors', // Avoid CORS errors for external services; opaque response still proves reachability
    });

    const latency = Math.round(performance.now() - startTime);

    // With no-cors mode, response.type is "opaque" and status is always 0,
    // but a successful fetch (no exception) means the service is reachable.
    // Only exceptions (AbortError, TypeError) indicate the service is unreachable.
    let status: ServiceHealthStatus;
    let suggestion: string | undefined;

    if (latency < PERFORMANCE_THRESHOLDS.HEALTHY_LATENCY) {
      status = 'healthy';
    } else if (latency < PERFORMANCE_THRESHOLDS.DEGRADED_LATENCY) {
      status = 'degraded';
      suggestion = `Response time (${latency}ms) is slower than optimal. ${COMMON_SUGGESTIONS.SLOW_NETWORK}`;
    } else {
      status = 'degraded';
      suggestion = `Response time (${latency}ms) is very slow. ${COMMON_SUGGESTIONS.HIGH_LATENCY}`;
    }

    logger.info(`[HEALTH-CHECK] ${serviceInfo.name}: ${status} (${latency}ms)`);

    return {
      service: serviceInfo.id,
      serviceName: serviceInfo.name,
      serviceUrl: serviceInfo.url,
      status,
      latency,
      timestamp: new Date().toISOString(),
      suggestion,
    };
  } catch (error) {
    const latency = Math.round(performance.now() - startTime);

    let status: ServiceHealthStatus = 'down';
    let errorMessage = 'Service unreachable';
    let suggestion: string | undefined;

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        status = 'down';
        errorMessage = 'Request timed out';
        suggestion = COMMON_SUGGESTIONS.SLOW_NETWORK;
      } else if (
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError')
      ) {
        status = 'blocked';
        errorMessage = 'Connection blocked';
        suggestion = COMMON_SUGGESTIONS.FIREWALL_BLOCKING;
      } else {
        errorMessage = error.message;
      }
    }

    logger.warn(`[HEALTH-CHECK] ${serviceInfo.name}: ${status} — ${errorMessage}`);

    return {
      service: serviceInfo.id,
      serviceName: serviceInfo.name,
      serviceUrl: serviceInfo.url,
      status,
      latency,
      timestamp: new Date().toISOString(),
      error: errorMessage,
      suggestion,
    };
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Get network information from browser APIs
 */
const getNetworkInfo = () => {
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  return {
    isOnline: navigator.onLine,
    effectiveBandwidth: connection?.effectiveType || undefined,
    downlink: connection?.downlink || undefined,
    rtt: connection?.rtt || undefined,
  };
};

/**
 * Detect common network issues (VPN, firewall, bandwidth, latency, DNS)
 */
const detectIssues = (
  healthChecks: ServiceHealthCheck[],
  networkInfo: ReturnType<typeof getNetworkInfo>
): HealthCheckReport['detectedIssues'] => {
  const issues: HealthCheckReport['detectedIssues'] = [];

  const blockedServices = healthChecks.filter(s => s.status === 'blocked');
  const slowServices = healthChecks.filter(
    s => s.latency && s.latency > PERFORMANCE_THRESHOLDS.DEGRADED_LATENCY
  );
  const fastServices = healthChecks.filter(
    s => s.latency && s.latency < PERFORMANCE_THRESHOLDS.HEALTHY_LATENCY
  );
  const servicesWithLatency = healthChecks.filter(s => s.latency !== undefined);

  const latencies = servicesWithLatency.map(s => s.latency!);
  const avgLatency =
    latencies.length > 0
      ? Math.round(latencies.reduce((sum, l) => sum + l, 0) / latencies.length)
      : 0;
  const maxLatency = latencies.length > 0 ? Math.max(...latencies) : 0;
  const minLatency = latencies.length > 0 ? Math.min(...latencies) : 0;
  const latencyVariance = maxLatency - minLatency;

  // Firewall detection
  if (blockedServices.length > 0) {
    let firewallConfidence: 'none' | 'low' | 'medium' | 'high' | 'very-high' = 'medium';
    let firewallType = 'corporate firewall or network restriction';

    if (blockedServices.length >= 2) {
      firewallConfidence = 'high';
      firewallType = 'corporate firewall (multiple services blocked)';
    }

    if (blockedServices.length === healthChecks.length) {
      firewallConfidence = 'very-high';
      firewallType = 'strict firewall or internet disconnect';
    }

    const openAIBlocked = blockedServices.some(s => s.service === 'openai');
    const workosOK = healthChecks.find(s => s.service === 'workos')?.status === 'healthy';

    if (openAIBlocked && workosOK) {
      firewallConfidence = 'very-high';
      firewallType = 'AI service blocked (common in enterprise networks)';
    }

    issues.push({
      type: 'firewall',
      severity: blockedServices.length >= 2 ? 'high' : 'medium',
      description: `${blockedServices.length} service(s) blocked: ${blockedServices.map(s => s.serviceName).join(', ')}. Detected ${firewallType}.`,
      suggestion: COMMON_SUGGESTIONS.FIREWALL_BLOCKING,
      confidence: firewallConfidence,
    });
  }

  // VPN detection
  if (servicesWithLatency.length >= 2) {
    let vpnConfidence: 'none' | 'low' | 'medium' | 'high' | 'very-high' = 'none';
    const vpnIndicators: string[] = [];

    const consistentLatency = latencyVariance < 100 && avgLatency > 150;
    if (consistentLatency) {
      vpnIndicators.push('uniform latency overhead');
      vpnConfidence = 'low';
    }

    const allModeratelySlow =
      slowServices.length >= 2 && slowServices.length === servicesWithLatency.length;
    if (allModeratelySlow) {
      vpnIndicators.push('all services affected equally');
      vpnConfidence = vpnConfidence === 'low' ? 'medium' : 'low';
    }

    const highMinLatency = minLatency > 80 && avgLatency > 150;
    if (highMinLatency) {
      vpnIndicators.push(`baseline latency ${minLatency}ms`);
      vpnConfidence =
        vpnConfidence === 'medium' ? 'high' : vpnConfidence === 'low' ? 'medium' : 'low';
    }

    if (networkInfo.rtt && networkInfo.rtt > 100 && networkInfo.rtt < 300) {
      vpnIndicators.push('elevated RTT');
      vpnConfidence =
        vpnConfidence === 'high'
          ? 'very-high'
          : vpnConfidence === 'medium'
            ? 'high'
            : vpnConfidence === 'low'
              ? 'medium'
              : 'low';
    }

    if (vpnConfidence !== 'none' && vpnConfidence !== 'low') {
      const confidenceLabel =
        vpnConfidence === 'very-high'
          ? 'Very likely'
          : vpnConfidence === 'high'
            ? 'Likely'
            : 'Possibly';

      issues.push({
        type: 'vpn',
        severity: vpnConfidence === 'very-high' || vpnConfidence === 'high' ? 'medium' : 'low',
        description: `${confidenceLabel} using VPN (${vpnIndicators.join(', ')}). Avg latency: ${avgLatency}ms.`,
        suggestion: COMMON_SUGGESTIONS.VPN_DETECTED,
        confidence: vpnConfidence,
      });
    }
  }

  // High RTT
  if (networkInfo.rtt && networkInfo.rtt > PERFORMANCE_THRESHOLDS.VERY_HIGH_RTT) {
    issues.push({
      type: 'latency',
      severity: 'high',
      description: `Very high network latency detected (${networkInfo.rtt}ms)`,
      suggestion: COMMON_SUGGESTIONS.HIGH_LATENCY,
    });
  } else if (networkInfo.rtt && networkInfo.rtt > PERFORMANCE_THRESHOLDS.HIGH_RTT) {
    issues.push({
      type: 'latency',
      severity: 'medium',
      description: `High network latency detected (${networkInfo.rtt}ms)`,
      suggestion: COMMON_SUGGESTIONS.HIGH_LATENCY,
    });
  }

  // Slow bandwidth
  if (networkInfo.effectiveBandwidth === 'slow-2g' || networkInfo.effectiveBandwidth === '2g') {
    issues.push({
      type: 'bandwidth',
      severity: 'high',
      description: 'Very slow network connection detected (2G speed)',
      suggestion: COMMON_SUGGESTIONS.SLOW_NETWORK,
    });
  } else if (networkInfo.effectiveBandwidth === '3g') {
    issues.push({
      type: 'bandwidth',
      severity: 'medium',
      description: 'Slow network connection detected (3G speed)',
      suggestion: COMMON_SUGGESTIONS.SLOW_NETWORK,
    });
  }

  // DNS: only one service is slow while others are fast
  if (slowServices.length === 1 && fastServices.length >= 2) {
    issues.push({
      type: 'dns',
      severity: 'low',
      description: `Only ${slowServices[0].serviceName} is slow, which may indicate a DNS issue for that specific service.`,
      suggestion: COMMON_SUGGESTIONS.DNS_ISSUES,
    });
  }

  return issues;
};

/**
 * Determine overall health status from individual service checks
 */
const determineOverallStatus = (healthChecks: ServiceHealthCheck[]): ServiceHealthStatus => {
  const criticalServices = healthChecks.filter(check => SERVICE_INFO[check.service].critical);

  if (criticalServices.some(s => s.status === 'down' || s.status === 'blocked')) {
    return 'down';
  }

  if (criticalServices.some(s => s.status === 'degraded')) {
    return 'degraded';
  }

  if (criticalServices.every(s => s.status === 'healthy')) {
    return 'healthy';
  }

  return 'unknown';
};

/**
 * Run health checks on all critical services in parallel
 */
export async function runHealthChecks(
  servicesToCheck: ExternalService[] = ['openai', 's3', 'workos']
): Promise<HealthCheckReport> {
  logger.info('[HEALTH-CHECK] Running diagnostics on services:', servicesToCheck);

  const healthChecks = await Promise.all(
    servicesToCheck.map(service => testServiceHealth(service))
  );
  const networkInfo = getNetworkInfo();
  const detectedIssues = detectIssues(healthChecks, networkInfo);
  const overallStatus = determineOverallStatus(healthChecks);

  const report: HealthCheckReport = {
    timestamp: new Date().toISOString(),
    overallStatus,
    services: healthChecks,
    networkInfo,
    detectedIssues,
  };

  logger.info('[HEALTH-CHECK] Diagnostics complete:', {
    overallStatus,
    unhealthyServices: healthChecks.filter(s => s.status !== 'healthy').length,
    issuesDetected: detectedIssues.length,
  });

  return report;
}

/**
 * Format health report for display
 */
export function formatHealthReport(report: HealthCheckReport): string {
  const lines = [`Overall: ${report.overallStatus}`, ''];
  report.services.forEach(service => {
    const latencyStr = service.latency ? ` (${service.latency}ms)` : '';
    lines.push(`${service.serviceName}: ${service.status}${latencyStr}`);
  });
  if (report.detectedIssues.length > 0) {
    lines.push('', 'Issues:');
    report.detectedIssues.forEach(issue => {
      lines.push(`- ${issue.description}`);
    });
  }
  return lines.join('\n');
}

/**
 * Get health status explanation
 */
export function getHealthStatusExplanation(report: HealthCheckReport): string {
  if (report.overallStatus === 'healthy') {
    return "All services are working normally. If you're experiencing slowness, it's likely due to AI processing time, not network issues.";
  }

  if (report.overallStatus === 'degraded') {
    const slowServices = report.services.filter(s => s.status === 'degraded');
    return `Some services are responding slowly: ${slowServices.map(s => s.serviceName).join(', ')}. This may cause delays in responses.`;
  }

  if (report.overallStatus === 'down') {
    const downServices = report.services.filter(s => s.status === 'down' || s.status === 'blocked');
    return `Unable to reach critical services: ${downServices.map(s => s.serviceName).join(', ')}. The app may not function properly.`;
  }

  return 'Unable to determine service health. Please check your internet connection.';
}
